// src/features/streams/StreamScheduleUtils.ts

import { DateTime, type DurationLikeObject } from 'luxon';
import {
  MediaStreamScheduleType,
  type IMediaStreamScheduleAllData,
  type MediaStreamScheduleData,
  type MediaStreamScheduleIntervalType,
} from '@lib2/model/stream/MediaStreamScheduleTypes';
import {
  SCHEDULE_INTERVAL_TYPE_LABELS,
  SCHEDULE_INTERVAL_UNIT_LABELS,
  type ScheduleIntervalType,
} from '@/lib/constants/stream';
import type { ScheduleFormData } from './schemas/ScheduleForm.schema';

interface ScheduleWindow {
  startUtc: number;
  endUtc: number;
}

/** Structural recurrence input — satisfied by wire MediaStreamScheduleData and by form-derived objects */
interface RecurringWindow extends ScheduleWindow {
  interval: number | null;
  intervalType: MediaStreamScheduleIntervalType | ScheduleIntervalType | null;
}

export interface PartitionedSchedules {
  /** Record schedules that are upcoming or recurring, sorted by next occurrence asc */
  active: IMediaStreamScheduleAllData[];
  /** Expired one-time record schedules, sorted by startUtc desc (newest first) */
  expired: IMediaStreamScheduleAllData[];
  /** Non-record schedules (e.g. 'view') — never displayed, carried through every save */
  other: IMediaStreamScheduleAllData[];
}

/** Mantine DateTimePicker string value format */
const DATE_STR_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/** Display formatting is app-locale (English), independent of machine locale */
const DISPLAY_LOCALE = 'en-US';

/** Conflict detection expands recurring schedules this far out (matches legacy) */
const CONFLICT_HORIZON_YEARS = 2;

/**
 * Pure schedule logic for Scheduled Record. All schedule times are wall-clock
 * in the stream's scheduleZoneId — every conversion takes an explicit zone, and
 * every "now"-dependent function takes an explicit nowSecs (epoch seconds) so
 * behavior is deterministic and testable.
 *
 * Recurrence is anchored to local wall-clock in the schedule zone: occurrences
 * are projected with Luxon `plus()` in that zone, so a 9:00 AM weekly schedule
 * stays 9:00 AM across DST transitions (same semantics as lib2
 * MediaStreamSchedules).
 */
export class StreamScheduleUtils {
  // --- Zone-aware picker conversions ---

  /** Picker string (wall-clock in zoneId) → UTC epoch seconds. NaN when unparseable. */
  static dateStrToUtcSeconds(dateStr: string, zoneId: string): number {
    const dt = DateTime.fromFormat(dateStr, DATE_STR_FORMAT, { zone: zoneId });
    return dt.isValid ? Math.floor(dt.toSeconds()) : NaN;
  }

  /** UTC epoch seconds → picker string (wall-clock in zoneId). */
  static utcSecondsToDateStr(ts: number, zoneId: string): string {
    return DateTime.fromSeconds(ts, { zone: zoneId }).toFormat(DATE_STR_FORMAT);
  }

  // --- Display ---

  /** 'One-time' | 'Daily' | 'Every 2 weeks' | 'Monthly' | ... */
  static getFrequencyLabel(
    interval: number | null,
    intervalType: MediaStreamScheduleIntervalType | ScheduleIntervalType | null,
  ): string {
    if (interval == null || intervalType == null) return 'One-time';
    const type = intervalType as ScheduleIntervalType;
    if (interval > 1) return `Every ${interval} ${SCHEDULE_INTERVAL_UNIT_LABELS[type]}s`;
    return SCHEDULE_INTERVAL_TYPE_LABELS[type];
  }

  /** 'Sun, Aug 8 · 9:00 AM – 12:00 PM' (end date included when the window crosses midnight) */
  static formatScheduleWindow(s: ScheduleWindow, zoneId: string): string {
    const start = DateTime.fromSeconds(s.startUtc, { zone: zoneId }).setLocale(DISPLAY_LOCALE);
    const end = DateTime.fromSeconds(s.endUtc, { zone: zoneId }).setLocale(DISPLAY_LOCALE);
    const startStr = `${start.toFormat('ccc, MMM d')} · ${start.toFormat('h:mm a')}`;
    if (start.hasSame(end, 'day')) {
      return `${startStr} – ${end.toFormat('h:mm a')}`;
    }
    return `${startStr} – ${end.toFormat('ccc, MMM d')} · ${end.toFormat('h:mm a')}`;
  }

  /** 'Sun, Aug 8 · 9:00 AM' — single instant in the schedule zone */
  static formatOccurrence(ts: number, zoneId: string): string {
    const dt = DateTime.fromSeconds(ts, { zone: zoneId }).setLocale(DISPLAY_LOCALE);
    return `${dt.toFormat('ccc, MMM d')} · ${dt.toFormat('h:mm a')}`;
  }

  /** '2h 30m' | '1h' | '45m'; '' when the range is empty or inverted */
  static formatDuration(startUtc: number, endUtc: number): string {
    const totalMins = Math.round((endUtc - startUtc) / 60);
    if (totalMins <= 0) return '';
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  }

  // --- Recurrence projection ---

  /**
   * Next window (in-progress or upcoming) of a schedule.
   * One-time: its window while endUtc > nowSecs, else null (expired).
   * Recurring: first projected occurrence whose end is still in the future.
   */
  static getNextOccurrence(
    s: RecurringWindow,
    zoneId: string,
    nowSecs: number,
  ): ScheduleWindow | null {
    if (s.interval == null || s.intervalType == null) {
      return s.endUtc > nowSecs ? { startUtc: s.startUtc, endUtc: s.endUtc } : null;
    }
    const step = StreamScheduleUtils.toDuration(s.interval, s.intervalType);
    let start = DateTime.fromSeconds(s.startUtc, { zone: zoneId });
    let end = DateTime.fromSeconds(s.endUtc, { zone: zoneId });
    while (end.toSeconds() <= nowSecs) {
      start = start.plus(step);
      end = end.plus(step);
    }
    return { startUtc: Math.floor(start.toSeconds()), endUtc: Math.floor(end.toSeconds()) };
  }

  /** Start instants of the next `count` occurrences (empty for expired one-time). */
  static getUpcomingOccurrences(
    s: RecurringWindow,
    zoneId: string,
    nowSecs: number,
    count: number,
  ): number[] {
    if (s.interval == null || s.intervalType == null) {
      return s.endUtc > nowSecs ? [s.startUtc] : [];
    }
    const step = StreamScheduleUtils.toDuration(s.interval, s.intervalType);
    let start = DateTime.fromSeconds(s.startUtc, { zone: zoneId });
    let end = DateTime.fromSeconds(s.endUtc, { zone: zoneId });
    while (end.toSeconds() <= nowSecs) {
      start = start.plus(step);
      end = end.plus(step);
    }
    const out: number[] = [];
    while (out.length < count) {
      out.push(Math.floor(start.toSeconds()));
      start = start.plus(step);
    }
    return out;
  }

  // --- State ---

  /** One-time schedule whose window has fully passed. Recurring schedules never expire. */
  static isExpired(s: MediaStreamScheduleData, nowSecs: number): boolean {
    return s.interval == null && s.intervalType == null && s.endUtc < nowSecs;
  }

  /** Split the wire array into displayable record schedules and pass-through rest. */
  static partitionSchedules(
    all: IMediaStreamScheduleAllData[],
    zoneId: string,
    nowSecs: number,
  ): PartitionedSchedules {
    const other = all.filter((s) => s.schedule.scheduleType !== MediaStreamScheduleType.Record);
    const record = all.filter((s) => s.schedule.scheduleType === MediaStreamScheduleType.Record);
    const expired = record
      .filter((s) => StreamScheduleUtils.isExpired(s.schedule, nowSecs))
      .sort((a, b) => b.schedule.startUtc - a.schedule.startUtc);
    const active = record
      .filter((s) => !StreamScheduleUtils.isExpired(s.schedule, nowSecs))
      .sort((a, b) => {
        const nextA = StreamScheduleUtils.getNextOccurrence(a.schedule, zoneId, nowSecs);
        const nextB = StreamScheduleUtils.getNextOccurrence(b.schedule, zoneId, nowSecs);
        return (nextA?.startUtc ?? Infinity) - (nextB?.startUtc ?? Infinity);
      });
    return { active, expired, other };
  }

  // --- Conflict detection ---

  /**
   * First sibling whose projected occurrences strictly overlap the candidate's
   * within a 2-year horizon; null when the candidate fits. Adjacent windows
   * (end == next start) do NOT conflict. When editing, pass editedOriginal so
   * the schedule doesn't conflict with itself (excluded by scheduleId — every
   * editable row is server-persisted in the instant-save model).
   */
  static findConflict(
    candidate: IMediaStreamScheduleAllData,
    siblings: IMediaStreamScheduleAllData[],
    zoneId: string,
    nowSecs: number,
    editedOriginal?: IMediaStreamScheduleAllData,
  ): IMediaStreamScheduleAllData | null {
    const horizonSecs = Math.floor(
      DateTime.fromSeconds(nowSecs, { zone: zoneId }).plus({ years: CONFLICT_HORIZON_YEARS }).toSeconds(),
    );
    const candidateWindows = StreamScheduleUtils.projectWindows(candidate.schedule, zoneId, nowSecs, horizonSecs);
    if (candidateWindows.length === 0) return null;

    for (const sibling of siblings) {
      if (editedOriginal && sibling.schedule.scheduleId === editedOriginal.schedule.scheduleId) continue;
      const siblingWindows = StreamScheduleUtils.projectWindows(sibling.schedule, zoneId, nowSecs, horizonSecs);
      if (StreamScheduleUtils.anyOverlap(candidateWindows, siblingWindows)) return sibling;
    }
    return null;
  }

  // --- Form boundary transforms ---

  /** Defaults for a new schedule: next full hour in the schedule zone, 1-hour duration. */
  static createDefaultFormData(zoneId: string, nowSecs: number): ScheduleFormData {
    const start = DateTime.fromSeconds(nowSecs, { zone: zoneId }).startOf('hour').plus({ hours: 1 });
    const end = start.plus({ hours: 1 });
    return {
      startStr: start.toFormat(DATE_STR_FORMAT),
      endStr: end.toFormat(DATE_STR_FORMAT),
      repeats: 'never',
      interval: 1,
      overrideTitle: false,
      title: '',
      overrideDesc: false,
      desc: '',
      overrideTags: false,
      tags: [],
    };
  }

  static scheduleToFormData(s: IMediaStreamScheduleAllData, zoneId: string): ScheduleFormData {
    const sch = s.schedule;
    const repeats = sch.interval != null && sch.intervalType != null
      ? (sch.intervalType as ScheduleIntervalType)
      : 'never';
    return {
      startStr: StreamScheduleUtils.utcSecondsToDateStr(sch.startUtc, zoneId),
      endStr: StreamScheduleUtils.utcSecondsToDateStr(sch.endUtc, zoneId),
      repeats,
      interval: sch.interval ?? 1,
      // Contract quirk: title has no boolean flag — mediaTitle != null IS the flag
      overrideTitle: sch.mediaTitle != null,
      title: sch.mediaTitle ?? '',
      overrideDesc: sch.mediaOverrideDescShort,
      desc: sch.mediaDescShort ?? '',
      overrideTags: sch.mediaOverrideTags,
      tags: s.tags ?? [],
    };
  }

  /**
   * Form → wire schedule. Preserves original.schedule.scheduleId on edit
   * (0 on create; server assigns the real id).
   */
  static formDataToSchedule(
    form: ScheduleFormData,
    zoneId: string,
    mediaId: number,
    nowSecs: number,
    original?: IMediaStreamScheduleAllData,
  ): IMediaStreamScheduleAllData {
    const recurring = form.repeats !== 'never';
    return {
      schedule: {
        scheduleId: original?.schedule.scheduleId ?? 0,
        mediaId,
        startUtc: StreamScheduleUtils.dateStrToUtcSeconds(form.startStr, zoneId),
        endUtc: StreamScheduleUtils.dateStrToUtcSeconds(form.endStr, zoneId),
        interval: recurring ? form.interval : null,
        intervalType: recurring ? (form.repeats as MediaStreamScheduleIntervalType) : null,
        scheduleType: MediaStreamScheduleType.Record,
        // Contract quirk: no boolean flag for title — null means "inherit stream title"
        mediaTitle: form.overrideTitle ? form.title.trim() : null,
        mediaDescShort: form.overrideDesc ? (form.desc || null) : null,
        mediaOverrideDescShort: form.overrideDesc,
        mediaOverrideTags: form.overrideTags,
        updated: nowSecs,
      },
      tags: form.overrideTags ? form.tags : [],
    };
  }

  /**
   * Copy of a schedule shifted to the next occurrence of `weekday` (Luxon
   * 1=Monday … 7=Sunday) on/after its own start, preserving wall-clock start
   * and end times in the schedule zone (day-based Luxon math is DST-safe).
   * Multi-day weekly creation generates one weekly schedule per weekday.
   */
  static shiftToWeekday(
    s: IMediaStreamScheduleAllData,
    weekday: number,
    zoneId: string,
  ): IMediaStreamScheduleAllData {
    const start = DateTime.fromSeconds(s.schedule.startUtc, { zone: zoneId });
    const deltaDays = (weekday - start.weekday + 7) % 7;
    if (deltaDays === 0) return s;
    const end = DateTime.fromSeconds(s.schedule.endUtc, { zone: zoneId });
    return {
      ...s,
      schedule: {
        ...s.schedule,
        startUtc: Math.floor(start.plus({ days: deltaDays }).toSeconds()),
        endUtc: Math.floor(end.plus({ days: deltaDays }).toSeconds()),
      },
    };
  }

  // --- Internals ---

  private static toDuration(
    interval: number,
    intervalType: MediaStreamScheduleIntervalType | ScheduleIntervalType,
  ): DurationLikeObject {
    switch (intervalType as ScheduleIntervalType) {
      case 'd': return { days: interval };
      case 'w': return { weeks: interval };
      case 'm': return { months: interval };
      case 'y': return { years: interval };
      default: throw new Error(`Unsupported intervalType: ${intervalType}`);
    }
  }

  /** Windows of a schedule intersecting [nowSecs, horizonSecs], ascending. */
  private static projectWindows(
    s: RecurringWindow,
    zoneId: string,
    nowSecs: number,
    horizonSecs: number,
  ): ScheduleWindow[] {
    if (s.interval == null || s.intervalType == null) {
      return s.endUtc > nowSecs ? [{ startUtc: s.startUtc, endUtc: s.endUtc }] : [];
    }
    const step = StreamScheduleUtils.toDuration(s.interval, s.intervalType);
    let start = DateTime.fromSeconds(s.startUtc, { zone: zoneId });
    let end = DateTime.fromSeconds(s.endUtc, { zone: zoneId });
    while (end.toSeconds() <= nowSecs) {
      start = start.plus(step);
      end = end.plus(step);
    }
    const windows: ScheduleWindow[] = [];
    while (start.toSeconds() <= horizonSecs) {
      windows.push({ startUtc: Math.floor(start.toSeconds()), endUtc: Math.floor(end.toSeconds()) });
      start = start.plus(step);
      end = end.plus(step);
    }
    return windows;
  }

  /** Strict overlap between two ascending window lists (touching edges don't overlap). */
  private static anyOverlap(a: ScheduleWindow[], b: ScheduleWindow[]): boolean {
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
      if (a[i].startUtc < b[j].endUtc && a[i].endUtc > b[j].startUtc) return true;
      if (a[i].endUtc <= b[j].endUtc) {
        i++;
      } else {
        j++;
      }
    }
    return false;
  }
}
